import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { API_URL, resolveBackendPath, resolveUploadsPath, getLocalThumbnailUrl, getLocalImageUrl, getDriveThumbnailUrl, getDriveImageUrl } from '../config';
import { useBackgroundJobs } from '../context/BackgroundJobsContext';

const StatusBadge = ({ status }) => {
  const map = {
    pending:    'bg-amber-100 text-amber-700 border-amber-200',
    processing: 'bg-blue-100 text-blue-700 border-blue-200',
    ready:      'bg-emerald-100 text-emerald-700 border-emerald-200',
    approved:   'bg-emerald-100 text-emerald-700 border-emerald-200',
    rejected:   'bg-rose-100 text-rose-700 border-rose-200',
  };
  return (
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border capitalize flex-shrink-0 ${map[status] || 'bg-slate-100 text-slate-500 border-slate-200'}`}>
      {status}
    </span>
  );
};

const UserBatches = () => {
  const { addEncodingJob, addDeletionJob } = useBackgroundJobs();
  const location = useLocation();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [encodingInProgress, setEncodingInProgress] = useState(false);
  const [encodingProgress, setEncodingProgress] = useState(null);
  const [photoFilter, setPhotoFilter] = useState('all');
  const [eventFilter, setEventFilter] = useState('');
  const batchDetailsRequestIdRef = useRef(null);

  const eventOptions = React.useMemo(() => {
    const opts = [{ value: '', label: 'All events' }];
    const seen = new Set();
    batches.forEach((b) => {
      const id = b.event_id != null ? b.event_id : '__none__';
      if (seen.has(id)) return;
      seen.add(id);
      opts.push({ value: id, label: b.event_name || 'No event' });
    });
    return opts;
  }, [batches]);

  const filteredBatches = React.useMemo(() => {
    if (!eventFilter) return batches;
    if (eventFilter === '__none__') return batches.filter((b) => b.event_id == null);
    return batches.filter((b) => b.event_id != null && Number(b.event_id) === Number(eventFilter));
  }, [batches, eventFilter]);

  const pendingCount = React.useMemo(() => filteredBatches.filter(b => b.status === 'pending').length, [filteredBatches]);

  const displayStatus = (batch) => {
    if (!batch) return '';
    if (batch.status === 'pending') return 'pending';
    if (batch.status === 'approved') {
      if (batch.approved_count > 0 && batch.unencoded_count > 0) return 'processing';
      if (batch.approved_count > 0 && batch.unencoded_count === 0) return 'ready';
      return 'ready';
    }
    return batch.status;
  };

  const encodingBatchIdRef = useRef(null);
  const detailRef = useRef(null);

  useEffect(() => {
    fetchBatches(location.state && location.state.batchId ? location.state.batchId : null);
  }, [location.state]);

  useEffect(() => {
    const batchId = encodingBatchIdRef.current;
    if (encodingInProgress && batchId) {
      let pollCount = 0;
      const maxPolls = 60;
      const interval = setInterval(async () => {
        pollCount++;
        try {
          const response = await axios.get(`${API_URL}/batches/${batchId}`);
          const updatedBatch = response.data;
          if (updatedBatch?.photos) {
            const approvedPhotos = updatedBatch.photos.filter(p => p.status === 'approved');
            const encodedPhotos = approvedPhotos.filter(p => p.is_encoded);
            const total = approvedPhotos.length;
            const encoded = encodedPhotos.length;
            if (total > 0) {
              setEncodingProgress({ current: encoded, total, message: `Encoding photos… ${encoded}/${total} completed` });
              if (batchDetailsRequestIdRef.current === null || batchDetailsRequestIdRef.current === batchId) {
                setSelectedBatch(updatedBatch);
              }
              if (encoded === total) {
                setEncodingInProgress(false);
                setEncodingProgress(null);
                encodingBatchIdRef.current = null;
                fetchBatches();
              }
            }
          }
          fetchBatches();
        } catch (error) {
          console.error('Error polling batch status:', error);
        }
        if (pollCount >= maxPolls) {
          setEncodingInProgress(false);
          setEncodingProgress(null);
          encodingBatchIdRef.current = null;
        }
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [encodingInProgress]);

  const fetchBatches = async (initialBatchId = null) => {
    try {
      const response = await axios.get(`${API_URL}/events/batches`);
      setBatches(response.data);
      if (initialBatchId != null) {
        fetchBatchDetails(initialBatchId);
      }
    } catch (error) {
      console.error('Error fetching batches:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (batchId) => {
    if (!window.confirm('Approve this batch? Photos will be encoded.')) return;
    encodingBatchIdRef.current = batchId;
    setEncodingInProgress(true);
    setEncodingProgress({ current: 0, total: 0, message: 'Starting encoding...' });
    try {
      const response = await axios.post(`${API_URL}/batches/${batchId}/approve`);
      const data = response.data;
      setEncodingProgress(null);
      setEncodingInProgress(false);
      const total = selectedBatch?.photos?.length ?? 0;
      if (total > 0) {
        addEncodingJob({
          batchId,
          total,
          label: `Batch #${selectedBatch?.batch_number ?? batchId}${selectedBatch?.event_name ? ' - ' + selectedBatch.event_name : ''}`,
          eventName: selectedBatch?.event_name || null,
        });
      }
      if (data.failed && data.failed > 0) {
        const failedList = data.failed_photos?.map(p => p.filename).join(', ') || 'some photos';
        const reason = data.encoding_error_reason || data.failed_photos?.[0]?.error;
        alert(`${data.message}\n\nFailed photos: ${failedList}${reason ? `\n\nReason: ${reason}` : ''}\n\nYou can retry encoding for these photos individually.`);
      } else {
        alert(data.message || 'Batch approved and photos encoded!');
      }
      fetchBatches();
      if (selectedBatch?.id === batchId) fetchBatchDetails(batchId);
    } catch (error) {
      setEncodingProgress(null);
      setEncodingInProgress(false);
      alert('Error: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const handleDelete = async (batchId) => {
    const batch = batches.find(b => b.id === batchId);
    const batchInfo = batch ? `Batch #${batch.batch_number}${batch.event_name ? ' - ' + batch.event_name : ''}` : 'this batch';
    if (!window.confirm(`Are you sure you want to delete ${batchInfo}?\n\nThis will permanently delete:\n- The batch and all its photos\n- All photo files and thumbnails\n- All face encodings\n- All related database records\n\nThis action cannot be undone!`)) return;
    try {
      const response = await axios.delete(`${API_URL}/batches/${batchId}`);
      const data = response.data;
      const message = data.queued != null
        ? (data.message || 'Batch deletion queued. Deletion continues in the background.')
        : `Batch deleted successfully!\n\nDeleted: ${data.deleted_files} photo files, ${data.deleted_thumbnails} thumbnails${(data.errors && data.errors.length > 0) ? `\n\nErrors:\n${data.errors.join('\n')}` : ''}`;
      alert(message);
      if (selectedBatch?.id === batchId) setSelectedBatch(null);
      addDeletionJob({
        batchId,
        total: batch?.photo_count ?? (selectedBatch?.id === batchId ? selectedBatch?.photos?.length : undefined),
        label: batch ? `Batch #${batch.batch_number}${batch.event_name ? ' - ' + batch.event_name : ''}` : `Batch #${batchId}`,
        eventName: batch?.event_name || null,
      });
      if (data.queued != null) {
        setBatches(prev => prev.filter(b => b.id !== batchId));
      } else {
        fetchBatches();
      }
    } catch (error) {
      alert('Error: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const fetchBatchDetails = async (batchId) => {
    const requestId = batchId;
    batchDetailsRequestIdRef.current = requestId;
    setSelectedBatch((prev) => (prev?.id === batchId ? prev : { id: batchId, _loading: true }));
    setPhotoFilter('all');
    try {
      const response = await axios.get(`${API_URL}/batches/${batchId}`);
      if (batchDetailsRequestIdRef.current !== requestId) return;
      setSelectedBatch(response.data);
      if (detailRef.current) {
        detailRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch (error) {
      if (batchDetailsRequestIdRef.current !== requestId) return;
      console.error('Error fetching batch details:', error);
      setSelectedBatch((prev) => (prev?.id === batchId ? null : prev));
    } finally {
      if (batchDetailsRequestIdRef.current === requestId) batchDetailsRequestIdRef.current = null;
    }
  };

  const handlePhotoAction = async (photoId, action) => {
    try {
      await axios.post(`${API_URL}/photos/${photoId}/${action}`);
      alert(`Photo ${action}d!`);
      if (selectedBatch) fetchBatchDetails(selectedBatch.id);
      fetchBatches();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const handleRetryEncoding = async (photoId) => {
    if (!window.confirm('Retry encoding for this photo?')) return;
    try {
      const response = await axios.post(`${API_URL}/photos/${photoId}/retry-encoding`);
      alert(response.data.message || 'Encoding successful!');
      if (selectedBatch) fetchBatchDetails(selectedBatch.id);
      fetchBatches();
    } catch (error) {
      alert('Encoding failed: ' + (error.response?.data?.details || error.response?.data?.error || 'Unknown error'));
    }
  };

  const handleRetryBatchEncoding = async (batchId) => {
    if (!window.confirm('Retry encoding for all unencoded photos in this batch?')) return;
    encodingBatchIdRef.current = batchId;
    setEncodingInProgress(true);
    setEncodingProgress({ current: 0, total: 0, message: 'Retrying encoding...' });
    try {
      const response = await axios.post(`${API_URL}/batches/retry-encoding/${batchId}`);
      const data = response.data;
      setEncodingProgress(null);
      setEncodingInProgress(false);
      const total = selectedBatch?.photos?.filter((p) => p.status === 'approved' && !p.is_encoded).length ?? 0;
      if (total > 0) {
        addEncodingJob({
          batchId,
          total,
          label: `Batch #${selectedBatch?.batch_number ?? batchId}${selectedBatch?.event_name ? ' - ' + selectedBatch.event_name : ''}`,
          eventName: selectedBatch?.event_name || null,
        });
      }
      if (data.failed && data.failed > 0) {
        const failedList = data.failed_photos?.map(p => p.filename).join(', ') || 'some photos';
        const reason = data.encoding_error_reason || data.failed_photos?.[0]?.error;
        alert(`${data.message}\n\nFailed photos: ${failedList}${reason ? `\n\nReason: ${reason}` : ''}\n\nYou can retry encoding for these photos individually.`);
      } else {
        alert(data.message || 'Encoding retry completed!');
      }
      fetchBatches();
      if (selectedBatch?.id === batchId) fetchBatchDetails(batchId);
    } catch (error) {
      setEncodingProgress(null);
      setEncodingInProgress(false);
      alert('Error: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto flex justify-center items-center min-h-[40vh]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-500">Loading batches…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">

      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">My Batches</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {batches.length === 0
              ? 'No batches yet'
              : `${batches.length} batch${batches.length !== 1 ? 'es' : ''}${pendingCount > 0 ? ` · ${pendingCount} pending approval` : ''}`}
          </p>
        </div>
        {pendingCount > 0 && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-bold text-amber-800 bg-amber-100 border border-amber-300 animate-pulse">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            {pendingCount} pending approval
          </span>
        )}
      </div>

      {batches.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-12 text-center">
          <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #ede9fe, #e0e7ff)' }}>
            <svg className="w-8 h-8 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <h3 className="text-base font-semibold text-slate-800 mb-1">No batches yet</h3>
          <p className="text-sm text-slate-500 max-w-sm mx-auto">Batches appear here when guests upload photos via your event&apos;s upload link.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">

          {/* ── Left: Batch list ── */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">

              {/* Event filter */}
              {eventOptions.length > 1 && (
                <div className="px-4 py-3 border-b border-slate-100">
                  <label className="block text-xs font-semibold text-slate-500 mb-1.5">Filter by event</label>
                  <select
                    value={eventFilter}
                    onChange={(e) => setEventFilter(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 bg-slate-50 focus:border-violet-500 focus:ring-2 focus:ring-violet-200 outline-none transition"
                  >
                    {eventOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              )}

              {filteredBatches.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-slate-400">No batches for this event.</div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {filteredBatches.map((batch) => {
                    const isPending = batch.status === 'pending';
                    const isSelected = selectedBatch?.id === batch.id;
                    return (
                      <li
                        key={batch.id}
                        onClick={() => fetchBatchDetails(batch.id)}
                        className={`px-4 py-3 cursor-pointer transition-all duration-150 ${
                          isSelected
                            ? 'bg-violet-50 border-l-[3px] border-violet-500'
                            : isPending
                            ? 'bg-amber-50/60 hover:bg-amber-50 border-l-[3px] border-amber-400'
                            : 'hover:bg-slate-50 border-l-[3px] border-transparent'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-sm font-semibold text-slate-800">Batch #{batch.batch_number}</span>
                              {batch.event_name && (
                                <span className="text-xs text-violet-600 font-medium truncate max-w-[120px]">· {batch.event_name}</span>
                              )}
                            </div>
                            <p className="text-xs text-slate-400 mt-0.5">
                              {batch.photo_count} photo{batch.photo_count !== 1 ? 's' : ''}
                              {batch.rejected_count > 0 && ` · ${batch.rejected_count} rejected`}
                              {batch.pending_count > 0 && ` · ${batch.pending_count} pending`}
                            </p>
                          </div>
                          <StatusBadge status={displayStatus(batch)} />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          {/* ── Right: Batch detail ── */}
          <div ref={detailRef} className="lg:col-span-3">
            {selectedBatch ? (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                {selectedBatch._loading ? (
                  <div className="flex items-center justify-center py-20 gap-3">
                    <div className="w-6 h-6 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm text-slate-500">Loading batch…</span>
                  </div>
                ) : (
                  <>
                    {/* Detail header */}
                    <div className="px-5 pt-5 pb-4 border-b border-slate-100">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h2 className="text-base font-bold text-slate-900">Batch #{selectedBatch.batch_number}</h2>
                            {selectedBatch.event_name && (
                              <span className="text-xs font-medium text-violet-600 bg-violet-50 px-2 py-0.5 rounded-full border border-violet-200">{selectedBatch.event_name}</span>
                            )}
                            <StatusBadge status={displayStatus(selectedBatch)} />
                          </div>

                          {/* Processing progress summary (only while still processing) */}
                          {selectedBatch.photos && (() => {
                            const approvedPhotos = selectedBatch.photos.filter(p => p.status === 'approved');
                            const encoded = approvedPhotos.filter(p => p.is_encoded).length;
                            const total = approvedPhotos.length;
                            const unencoded = total - encoded;
                            if (total === 0 || unencoded === 0) return null; // hide when everything is ready
                            return (
                              <div className="mt-2">
                                <div className="flex items-center gap-2 mb-1.5">
                                  <span className="text-xs text-slate-500 font-medium">Processing</span>
                                  <span className="text-xs font-semibold text-emerald-600">{encoded} ready</span>
                                  {unencoded > 0 && <span className="text-xs font-semibold text-orange-500">{unencoded} processing</span>}
                                  <span className="text-xs text-slate-400 ml-auto">{encoded}/{total}</span>
                                </div>
                                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                  <div className="h-full bg-gradient-to-r from-violet-500 to-indigo-500 rounded-full transition-all duration-300" style={{ width: `${total > 0 ? (encoded / total) * 100 : 0}%` }} />
                                </div>
                              </div>
                            );
                          })()}
                        </div>

                        {/* Action buttons */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {selectedBatch.status === 'approved' && selectedBatch.photos?.some(p => p.status === 'approved' && !p.is_encoded) && (
                            <button
                              onClick={() => handleRetryBatchEncoding(selectedBatch.id)}
                              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold text-violet-700 bg-violet-50 border border-violet-200 hover:bg-violet-100 transition-colors"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                              Retry Encoding
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(selectedBatch.id)}
                            className="p-2 rounded-xl text-slate-400 hover:bg-rose-50 hover:text-rose-600 border border-slate-200 hover:border-rose-200 transition-colors"
                            title="Delete batch"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Encoding in-progress bar */}
                    {encodingInProgress && encodingProgress && (
                      <div className="mx-5 mt-4 p-3 bg-violet-50 border border-violet-200 rounded-xl">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-4 h-4 border-2 border-violet-600 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                          <span className="text-sm text-violet-700 font-medium flex-1">{encodingProgress.message || 'Encoding in progress…'}</span>
                          {encodingProgress.total > 0 && (
                            <span className="text-xs text-violet-500 font-semibold">{encodingProgress.current}/{encodingProgress.total}</span>
                          )}
                        </div>
                        {encodingProgress.total > 0 && (
                          <div className="h-1.5 bg-violet-200 rounded-full overflow-hidden">
                            <div className="h-full bg-violet-600 rounded-full transition-all duration-300" style={{ width: `${(encodingProgress.current / encodingProgress.total) * 100}%` }} />
                          </div>
                        )}
                      </div>
                    )}

                    {/* Photo filter tabs */}
                    {selectedBatch.photos && selectedBatch.photos.length > 0 && (
                      <div className="px-5 pt-4 pb-3 flex flex-wrap gap-1.5">
                        {[
                          { key: 'all',         label: 'All',        count: selectedBatch.photos.length,                                                       color: 'violet' },
                          { key: 'encoded',     label: 'Ready',      count: selectedBatch.photos.filter(p => p.is_encoded).length,                             color: 'blue'   },
                          { key: 'not_encoded', label: 'Processing', count: selectedBatch.photos.filter(p => p.status === 'approved' && !p.is_encoded).length, color: 'orange' },
                          { key: 'pending',     label: 'Pending',    count: selectedBatch.photos.filter(p => p.status === 'pending').length,                   color: 'amber'  },
                          { key: 'rejected',    label: 'Rejected',   count: selectedBatch.photos.filter(p => p.status === 'rejected').length,                  color: 'rose'   },
                        ].map(({ key, label, count, color }) => {
                          const active = photoFilter === key;
                          const colorMap = {
                            violet: active ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-slate-500 border-slate-200 hover:border-violet-300',
                            blue:   active ? 'bg-blue-500 text-white border-blue-500'     : 'bg-white text-slate-500 border-slate-200 hover:border-blue-300',
                            orange: active ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-slate-500 border-slate-200 hover:border-orange-300',
                            amber:  active ? 'bg-amber-500 text-white border-amber-500'   : 'bg-white text-slate-500 border-slate-200 hover:border-amber-300',
                            rose:   active ? 'bg-rose-500 text-white border-rose-500'     : 'bg-white text-slate-500 border-slate-200 hover:border-rose-300',
                          };
                          return (
                            <button
                              key={key}
                              onClick={() => setPhotoFilter(key)}
                              className={`px-3 py-1 rounded-lg text-xs font-semibold border transition-colors ${colorMap[color]}`}
                            >
                              {label} ({count})
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* Photo grid */}
                    <div className="px-5 pb-5">
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                        {(() => {
                          let filteredPhotos = selectedBatch.photos || [];
                          switch (photoFilter) {
                            case 'encoded':     filteredPhotos = filteredPhotos.filter(p => p.is_encoded); break;
                            case 'not_encoded': filteredPhotos = filteredPhotos.filter(p => p.status === 'approved' && !p.is_encoded); break;
                            case 'pending':     filteredPhotos = filteredPhotos.filter(p => p.status === 'pending'); break;
                            case 'rejected':    filteredPhotos = filteredPhotos.filter(p => p.status === 'rejected'); break;
                            default: break;
                          }
                          if (filteredPhotos.length === 0) {
                            return (
                              <div className="col-span-full text-center py-8 text-slate-400 text-sm">
                                No photos for the selected filter.
                              </div>
                            );
                          }
                          return filteredPhotos.map((photo) => (
                            <div key={photo.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-all group">
                              <div className="relative">
                                <img
                                  src={getLocalThumbnailUrl(photo) || (photo.drive_file_id ? getDriveThumbnailUrl(photo.drive_file_id) : resolveUploadsPath(photo.filepath))}
                                  alt={photo.filename}
                                  className="w-full h-32 object-cover"
                                  onError={(e) => {
                                    if (photo.drive_file_id) {
                                      e.target.style.display = 'none';
                                    } else {
                                      const altPath = resolveBackendPath(photo.filepath);
                                      if (altPath && e.target.src !== altPath) e.target.src = altPath;
                                      else e.target.style.display = 'none';
                                    }
                                  }}
                                />
                                <button
                                  onClick={(e) => { e.stopPropagation(); window.open(getLocalImageUrl(photo) || getDriveImageUrl(photo.drive_file_id) || resolveUploadsPath(photo.filepath), '_blank'); }}
                                  className="absolute top-1.5 right-1.5 p-1.5 rounded-lg bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                  title="View original"
                                >
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                </button>
                                {/* Status pill overlay */}
                                <div className="absolute bottom-1.5 left-1.5">
                                  <span
                                    className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                                      photo.status === 'rejected'
                                        ? 'bg-rose-500/90 text-white'
                                        : (photo.status === 'approved' && photo.is_encoded)
                                        ? 'bg-emerald-500/90 text-white'
                                        : 'bg-amber-500/90 text-white'
                                    }`}
                                  >
                                    {photo.status === 'rejected'
                                      ? 'rejected'
                                      : photo.status === 'pending'
                                      ? 'pending'
                                      : photo.status === 'approved' && !photo.is_encoded
                                      ? 'processing'
                                      : photo.status === 'approved' && photo.is_encoded
                                      ? 'ready'
                                      : photo.status}
                                  </span>
                                </div>
                              </div>
                              <div className="p-2 space-y-1.5">
                                <div className="flex gap-1">
                                  {photo.status === 'pending' && (
                                    <>
                                      <button onClick={(e) => { e.stopPropagation(); handlePhotoAction(photo.id, 'approve'); }} className="flex-1 py-1 rounded-lg text-[11px] font-semibold text-white bg-emerald-500 hover:bg-emerald-600 transition-colors">Approve</button>
                                      <button onClick={(e) => { e.stopPropagation(); handlePhotoAction(photo.id, 'reject'); }} className="flex-1 py-1 rounded-lg text-[11px] font-semibold text-white bg-rose-500 hover:bg-rose-600 transition-colors">Reject</button>
                                    </>
                                  )}
                                  {photo.status === 'approved' && !photo.is_encoded && (
                                    <button onClick={(e) => { e.stopPropagation(); handleRetryEncoding(photo.id); }} className="w-full py-1 rounded-lg text-[11px] font-semibold text-white bg-violet-500 hover:bg-violet-600 transition-colors">Retry</button>
                                  )}
                                </div>
                              </div>
                            </div>
                          ));
                        })()}
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-10 flex flex-col items-center justify-center text-center h-full min-h-[200px]">
                <div className="w-12 h-12 rounded-2xl mb-3 flex items-center justify-center bg-slate-100">
                  <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <p className="text-sm font-medium text-slate-600">Select a batch to view details</p>
                <p className="text-xs text-slate-400 mt-1">Click any batch on the left to inspect its photos</p>
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );
};

export default UserBatches;
